<template>
    <validation-observer ref="form" v-slot="{ meta }">
        <modal
            :id="modalId"
            title="Settings"
            scrollable
            ok-title="Save"
            :ok-disabled="!meta.valid || loading"
            @ok.prevent="save"
            @hide="hideModal"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-form @submit.prevent="save" v-if="values">
                <b-row>
                    <b-col>
                        <form-select
                            label="Code generation timing:"
                            v-model="values.generate_code_days_before_arrival"
                            :options="daysBeforeArrivalOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-select
                            label="Check-in / check-out offset time:"
                            description="Setting an offset time will allow for early check-ins and late check-outs without requiring any changes to the official check-in and check-out times on the reservation."
                            v-model="values.code_offset_hours"
                            :options="codeOffsetHoursOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-select
                            label="Code origin (wi-fi locks):"
                            v-model="values.code_origin"
                            :options="codeOriginOptions"
                        ></form-select>
                    </b-col>
                </b-row>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
    import isEmpty from 'lodash/isEmpty';
    import API from '../../../../api';
    import { DOOR_LOCKS_CONFIG_ENDPOINT } from '../../../../endpoints';
    import { formatResponseError } from '../../../../utils/api';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import FormSelect from '../../../generic/form-select/form-select.vue';
    import Modal from '../../../generic/modal/modal.vue';

    export default {
        name: 'DoorLockSettingsModalForm',
        components: {
            BusyModal,
            FormSelect,
            Modal,
        },
        props: {
            modalId: String,
        },
        mounted() {
            (async () => {
                this.loading = true;
                try {
                    await this.fetchConfig();
                    this.values = this.getInitialValues();
                } finally {
                    this.loading = false;
                }
            })();
        },
        data() {
            return {
                loading: false,
                config: null,
                values: null,
                daysBeforeArrivalOptions: [
                    { value: 0, text: 'Never' },
                    { value: 1, text: '1 day before guest arrival' },
                    { value: 2, text: '2 days before guest arrival' },
                    { value: 3, text: '3 days before guest arrival' },
                    { value: 4, text: '4 days before guest arrival' },
                    { value: 5, text: '5 days before guest arrival' },
                    { value: 6, text: '6 days before guest arrival' },
                    { value: 7, text: '1 week before guest arrival' },
                    { value: 14, text: '2 weeks before guest arrival' },
                    { value: 21, text: '3 weeks before guest arrival' },
                    { value: 28, text: '4 weeks before guest arrival' }
                ],
                codeOffsetHoursOptions: [
                    { value: 0, text: 'None' },
                    { value: 1, text: '1 hour' },
                    { value: 2, text: '2 hours' }
                ],
                codeOriginOptions: [
                    { value: 0, text: '4 digit code random' },
                    { value: 1, text: 'Last 4 digits of guest mobile phone' }
                ],
            }
        },
        methods: {
            getInitialValues() {
                return {
                    code_offset_hours: this.config.code_offset_hours || 0,
                    code_origin: this.config.code_origin || 0,
                    generate_code_days_before_arrival: this.config.generate_code_days_before_arrival || 0,
                }
            },
            hideModal() {
                this.$bvModal.hide(this.modalId);
            },
            async fetchConfig() {
                try {
                    const response = await API.get(DOOR_LOCKS_CONFIG_ENDPOINT());
                    this.config = response.data;
                } catch {
                    this.errorToast({text: 'Could not load config.'})
                }
            },
            async save() {
                this.loading = true;

                try {
                    await API.post(DOOR_LOCKS_CONFIG_ENDPOINT(), this.values);
                    this.successToast({text: 'Settings were successfully saved.'})
                    this.$nextTick(() => {
                        this.hideModal();
                    })
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        this.errorToast({text: generic})
                    }
                    if (!generic && !isEmpty(fields)) {
                        this.errorToast({text: 'Something went wrong while saving settings. Recheck your input data.'});
                    }
                } finally {
                    this.loading = false;
                }
            },
        }
    }
</script>